import type {
  TranscriptArraySpeakerParticipant,
  TranscriptSpeakerLabelKind,
  TranscriptSpeakerParticipantMetadata,
} from "@/modules/audio-recording/transcript-types";

type TranscriptSpeakerParticipantLike =
  | TranscriptSpeakerParticipantMetadata
  | TranscriptArraySpeakerParticipant
  | null
  | undefined;

export type TranscriptSpeakerDisplayLabelSource = "participant" | "generic" | "channel";

export type TranscriptSpeakerDisplayLabel = {
  label: string;
  source: TranscriptSpeakerDisplayLabelSource;
};

export type TranscriptSpeakerDisplayArgs = {
  channel?: number | null;
  speakerParticipant?: TranscriptSpeakerParticipantLike;
  speakerParticipantId?: number | string | null;
  speakerLabel?: number | string | null;
  speakerLabelKind?: TranscriptSpeakerLabelKind | null;
  includeParticipantEmail?: boolean;
};

const RECALL_DEFAULT_PARTICIPANT_NAMES = new Set(["host", "guest"]);
const RECALL_GENERIC_DIARIZATION_NAME = /^(?:[0-9]+|[A-Za-z])$/;
const RECALL_HYBRID_COMPOSITE_NAME = /^(\d+)-[A-Za-z0-9]+$/;
const RECALL_SPEAKER_LABEL_EXTRA_DATA_KEY = "recall_speaker_label";

export function normalizeSpeakerParticipantName(value: unknown): string | undefined {
  if (typeof value !== "string") return undefined;
  const trimmed = value.trim();
  return trimmed ? trimmed : undefined;
}

export function isRecallDefaultParticipantName(value: unknown): boolean {
  const name = normalizeSpeakerParticipantName(value);
  return Boolean(name && RECALL_DEFAULT_PARTICIPANT_NAMES.has(name.toLowerCase()));
}

export function isRecallNonIdentityParticipantName(value: unknown): boolean {
  const name = normalizeSpeakerParticipantName(value);
  if (!name) return false;
  return (
    isRecallDefaultParticipantName(name) ||
    RECALL_HYBRID_COMPOSITE_NAME.test(name) ||
    RECALL_GENERIC_DIARIZATION_NAME.test(name)
  );
}

export function resolveRecallNonIdentitySpeakerLabelFromExtraData(
  extraData: Record<string, unknown> | null | undefined
): string | undefined {
  const label = normalizeSpeakerParticipantName(extraData?.[RECALL_SPEAKER_LABEL_EXTRA_DATA_KEY]);
  if (!label || !isRecallNonIdentityParticipantName(label)) return undefined;
  return label;
}

export function sanitizeRecallSpeakerLabelExtraData(
  extraData: Record<string, unknown> | null | undefined
): Record<string, unknown> | undefined {
  if (!extraData) return undefined;

  const sanitized = { ...extraData };
  if (isRecallNonIdentityParticipantName(sanitized[RECALL_SPEAKER_LABEL_EXTRA_DATA_KEY])) {
    delete sanitized[RECALL_SPEAKER_LABEL_EXTRA_DATA_KEY];
  }

  return Object.keys(sanitized).length > 0 ? sanitized : undefined;
}

export function extractRecallCompositeParticipantId(value: unknown): number | undefined {
  const name = normalizeSpeakerParticipantName(value);
  const match = name ? RECALL_HYBRID_COMPOSITE_NAME.exec(name) : null;
  if (!match) return undefined;
  const parsed = Number(match[1]);
  return Number.isFinite(parsed) ? parsed : undefined;
}

export function resolveTranscriptSpeakerDisplayLabel({
  channel,
  speakerParticipant,
  speakerLabel,
  speakerLabelKind,
  includeParticipantEmail = false,
}: TranscriptSpeakerDisplayArgs): TranscriptSpeakerDisplayLabel {
  const participantLabel = resolveDisplayableParticipantLabel(speakerParticipant, {
    includeParticipantEmail,
  });
  if (participantLabel) {
    return {
      label: participantLabel,
      source: "participant",
    };
  }

  if (speakerLabel != null && speakerLabelKind !== "channel") {
    return {
      label: formatGenericSpeakerLabel(speakerLabel),
      source: "generic",
    };
  }

  return {
    label: resolveChannelSpeakerLabel(channel),
    source: "channel",
  };
}

export function resolveTranscriptSpeakerLabel(args: TranscriptSpeakerDisplayArgs): string {
  return resolveTranscriptSpeakerDisplayLabel(args).label;
}

export function resolveTranscriptSpeakerGroupKey({
  channel,
  speakerParticipant,
  speakerParticipantId,
  speakerLabel,
  speakerLabelKind,
}: TranscriptSpeakerDisplayArgs): string {
  const participantId = normalizeIdentifier(speakerParticipant?.id ?? speakerParticipantId);
  if (participantId != null) return `participant:${String(participantId)}`;

  const participantEmail = normalizeSpeakerParticipantName(speakerParticipant?.email);
  if (participantEmail) return `participant-email:${participantEmail.toLowerCase()}`;

  const participantName = resolveDisplayableParticipantName(speakerParticipant);
  if (participantName) {
    return `participant-name:${participantName.toLowerCase()}`;
  }

  if (speakerLabel != null && speakerLabelKind !== "channel") {
    return `generic:${String(speakerLabel)}`;
  }

  return `channel:${channel ?? "unknown"}`;
}

function resolveDisplayableParticipantName(
  speakerParticipant: TranscriptSpeakerParticipantLike
): string | undefined {
  const participantName = normalizeSpeakerParticipantName(speakerParticipant?.name);
  if (!participantName || isRecallNonIdentityParticipantName(participantName)) {
    return undefined;
  }
  return participantName;
}

/**
 * Formats a real meeting participant label for transcript display.
 *
 * Callers can include email alongside the display name when they need stable
 * identity evidence in transcript text rather than a compact speaker badge.
 */
function resolveDisplayableParticipantLabel(
  speakerParticipant: TranscriptSpeakerParticipantLike,
  {
    includeParticipantEmail = false,
  }: {
    includeParticipantEmail?: boolean;
  } = {}
): string | undefined {
  const participantName = resolveDisplayableParticipantName(speakerParticipant);
  const participantEmail = normalizeSpeakerParticipantName(speakerParticipant?.email);
  if (participantName && includeParticipantEmail) {
    if (participantEmail && participantName.toLowerCase() === participantEmail.toLowerCase()) {
      return participantName;
    }
    return participantEmail ? `${participantName} - ${participantEmail}` : participantName;
  }

  return participantName ?? participantEmail;
}

function formatGenericSpeakerLabel(value: number | string): string {
  const normalized = String(value).trim();
  if (!normalized) return "Speaker";
  if (/^speaker\b/i.test(normalized)) return normalized;
  return `Speaker ${normalized}`;
}

function resolveChannelSpeakerLabel(channel: number | null | undefined): string {
  if (channel === 0) return "Me";
  if (typeof channel === "number" && channel < 0) return "Transcript";
  return "Other Speakers";
}

function normalizeIdentifier(value: unknown): number | string | undefined {
  if (typeof value === "number" && Number.isFinite(value)) return value;
  return normalizeSpeakerParticipantName(value);
}
